import { Accordion, Button, FormCheckbox } from "components/common"
import React, { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form"
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
//import UploadDocument from "components/common/upload/UploadDocument";
import DatePicker from "react-datepicker";
import { adjustToNoon } from 'helpers/utils';
import { BsBuildingAdd } from "react-icons/bs";
import AddBuildingForm from "./AddBuildingForm";
import { useNavigate } from "react-router-dom";
import { createWaiver, updateWaiver } from "redux/crm/project/waiverSlice";
import { MdDelete, MdEdit } from "react-icons/md";
import PropTypes from "prop-types";

import { INPUT_CLS as INPUT_CLASSES } from 'helpers/formStyles';
const CELL_BORDER = "tw-border tw-border-solid tw-border-slate-200 tw-px-2 tw-py-1.5";

const WaiverForm = ({ project, waiver, title }) => {
  const { control, register, handleSubmit, formState: { errors }, setValue, getValues, setError } = useForm()

  const numberOfKeys = 8;
  const defaultActiveKey = Array.from({ length: numberOfKeys }, (_, index) => index.toString());
  const [activeKey, setActiveKey] = useState(defaultActiveKey);

  const [showAddBuildingForm, setShowAddBuildingForm] = useState(false)
  const [waiverBuildings, setWaiverBuildings] = useState([])
  const [building, setBuilding] = useState(null)
  //const [files, setFiles] = useState([])

  const dispatch = useDispatch()
  const navigate = useNavigate()

  useEffect(() => {

    if (waiver) {
      Object.entries(waiver).forEach(([key, value]) => {
        switch (key) {
          case "released_for_order_material":
            setValue("released_for_order_material", value)
            setValue("released_for_order_material_check", value ?? false)
            break;

          case "commitment_date":
            setValue(key, new Date(value))
            break;

          case "buildings":
            setWaiverBuildings(value)
            break;

          default:
            setValue(key, value)
            break;
        }
      })

      return
    }

    // New waiver: prefill the contract figures from the project so the user
    // doesn't have to re-type them. Both value fields take the contract value;
    // both weight fields take the total JAF weight.
    const contract = project?.contract
    if (!contract) return

    const { final_contract_value: contractValue, final_jaf_weight: jafWeight } = contract

    if (contractValue != null) {
      setValue("main_contract_value", contractValue)
      setValue("waiver_value", contractValue)
    }

    if (jafWeight != null) {
      setValue("main_contract_weight", jafWeight)
      setValue("waiver_weight", jafWeight)
    }
  }, [waiver, project, setValue])

  const handleAccordionSelect = (newActiveKey) => {
    setActiveKey(newActiveKey)
  }

  const handleAddBuilding = () => {
    setBuilding(null)
    setShowAddBuildingForm(true)
  }

  const validateJobStatus = () => {
    if (!getValues("job_status_contract")
      && !getValues("job_status_loi")
      && !getValues("job_status_jaf")
      && !getValues("job_status_bom_jaf_comparison")
      && !getValues("job_status_other")) {
      toast.error("Job status must be required.")
      return false
    }

    return true
  }

  const validateReleaseFor = () => {
    const orderManagementOptions = [
      "released_for_anchor_bolt",
      "released_for_fabrication",
      "released_for_loading",
      "released_for_shipment",
      "released_for_order_material",
    ];

    const orderEntryOptions = [
      "released_for_shop",
      "released_for_apd_estimate",
      "released_for_other"
    ];

    const allOptions = [...orderManagementOptions, ...orderEntryOptions];

    // Check if at least one option is selected
    const hasAnySelection = allOptions.some(option => getValues(option));
    if (!hasAnySelection) {
      toast.error("At least one 'Release for' option must be selected.");
      return false;
    }

    // Get selected options for each group
    const selectedOrderManagement = orderManagementOptions.filter(option => getValues(option));
    const selectedOrderEntry = orderEntryOptions.filter(option => getValues(option));

    if (selectedOrderManagement.length > 0 && selectedOrderEntry.length > 0) {
      // Set errors on Order Management options
      selectedOrderManagement.forEach(option => {
        const optionName = option.replace(/released_for_/g, '').replace(/_/g, ' ');
        toast.error(`${optionName.toUpperCase()} is selected, no other options can be chosen.`);
      });

      // Set errors on Order Entry options
      selectedOrderEntry.forEach(option => {
        const optionName = option.replace(/released_for_/g, '').replace(/_/g, ' ');
        toast.error(`${optionName.toUpperCase()} is selected, no other options can be chosen.`);
      });

      return false;
    }

    if (getValues("released_for_shop") && getValues("released_for_apd_estimate")) {
      toast.error("Cannot select both Shop and APD Estimate options.");
      return false;
    }

    // Validate required specification fields
    if (getValues("released_for_order_material_check") && !getValues("released_for_order_material")) {
      setError("released_for_order_material", { message: "Released for order material must be specified" });
      return false;
    }

    if (getValues("released_for_other") && !getValues("released_for_other_specify")) {
      setError("released_for_other_specify", { message: "Released for other must be specified" });
      return false;
    }

    return true;
  };

  const validateWaiverValue = () => {
    if ((getValues("released_for_fabrication")
      || getValues("released_for_loading")
      || getValues("released_for_shipment"))
      && (getValues("waiver_value") === 0 || !getValues("waiver_value"))) {
      setError("waiver_value", { message: "Waiver value is required when fabrication, loading, or shipment is selected." });
      return false;
    }
    return true;
  };

  // Mirror the backend validations (main_contract_value / main_contract_weight
  // presence + greater_than: 0) so the user gets immediate inline feedback
  // instead of a rejected request.
  const validateMainContract = () => {
    let valid = true;
    const value = Number(getValues("main_contract_value"));
    const weight = Number(getValues("main_contract_weight"));

    if (!value || value <= 0) {
      setError("main_contract_value", { message: "Main contract value must be greater than 0." });
      valid = false;
    }
    if (!weight || weight <= 0) {
      setError("main_contract_weight", { message: "Main contract weight must be greater than 0." });
      valid = false;
    }
    return valid;
  };

  const onSubmit = async (data) => {
    const isJobStatusValid = validateJobStatus();
    const isReleaseForValid = validateReleaseFor();
    const isWaiverValueValid = validateWaiverValue();
    const isMainContractValid = validateMainContract();

    // If any validation fails, prevent submission
    if (!isJobStatusValid || !isReleaseForValid || !isWaiverValueValid || !isMainContractValid) {
      return; // Stop execution here if validation fails
    }

    data = { ...data, buildings: waiverBuildings }

    try {
      const projectId = project.id
      let waiverId = waiver?.id

      if (waiver) {
        await dispatch(updateWaiver({ projectId, waiverId, data })).unwrap()
      }
      else {
        // .unwrap() throws the rejectWithValue payload on failure so the real
        // server error surfaces below instead of crashing on `.data.id`.
        const response = await dispatch(createWaiver({ projectId, data })).unwrap()
        waiverId = response?.data?.id
      }

      if (waiverId) {
        navigate(`/crm/projects/${projectId}/waivers/${waiverId}`)
      }
    }
    catch (error) {
      // rejectWithValue payload is the server body, e.g. { error: ["Main contract weight must be greater than 0"] }
      const serverError = error?.error ?? error?.errors
      const message = Array.isArray(serverError)
        ? serverError.join(', ')
        : (serverError || error?.message || 'Failed to save waiver.')
      toast.error(message)
    }
  }

  const handleEditBuilding = (building) => {
    setBuilding(building)
    setShowAddBuildingForm(true)
  }

  const handleDeleteBuilding = (index) => {
    const newWaiverBuildings = waiverBuildings.filter((_, i) => i !== index)
    setWaiverBuildings(newWaiverBuildings)

    toast.success("Building was successful remove.")
  }

  const validateNumber = () => ({
    pattern: {
      value: /^[0-9]+(\.[0-9]{1,2})?$/,
      message: "Please enter a valid number with up to 2 decimal places"
    }
  });

  const fieldError = (error) => (
    <span className="tw-text-red-600 tw-text-xs">
      {error && error.message}
    </span>
  );

  const contractField = (label, name, placeholder, options = {}) => (
    <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-mb-2 tw-items-center">
      <div className="tw-col-span-12 md:tw-col-span-6">{label}</div>
      <div className="tw-col-span-12 sm:tw-col-span-9 md:tw-col-span-6">
        <input
          placeholder={placeholder}
          className={INPUT_CLASSES}
          {...register(name, options)}
        />
        {fieldError(errors[name])}
      </div>
    </div>
  );

  const jobStatusCheck = (label, name) => (
    <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-2 tw-items-center">
      <div className="tw-col-span-7 tw-text-sm">{label}</div>
      <div className="tw-col-span-5">
        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <FormCheckbox
              checked={field.value || false}
              onChange={(e) => field.onChange(e)}
            />
          )}
        />
      </div>
    </div>
  );

  const releaseForCheck = (label, name) => (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <FormCheckbox
          checked={field.value || false}
          label={label}
          onChange={(e) => field.onChange(e)}
        />
      )}
    />
  );

  const paymentTermField = (label, name, placeholder) => (
    <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-2 tw-items-center">
      <div className="tw-col-span-12 sm:tw-col-span-5 tw-text-sm lg:tw-text-left">{label}</div>
      <div className="tw-col-span-12 sm:tw-col-span-7">
        <div className="tw-flex tw-items-center">
          <input
            placeholder={placeholder}
            className={INPUT_CLASSES}
            {...register(name)}
          />
          <span className="tw-ml-1">%</span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="tw-bg-white tw-rounded tw-border tw-border-solid tw-border-slate-200 tw-shadow-sm tw-mb-3">
        <div className="tw-p-4 tw-text-sm">
          <h5 className="tw-text-center tw-text-2xl tw-font-semibold">{title} WAIVER FORM</h5>
        </div>
      </div>

      <div className="tw-bg-white tw-rounded tw-border tw-border-solid tw-border-slate-200 tw-shadow-sm tw-p-4">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Accordion
            id="revision-table"
            className="tw-text-sm"
            activeKeys={activeKey}
            onChange={handleAccordionSelect}
          >
            {/* 1. Project Information */}
            <Accordion.Item eventKey="0" header="1. Project Information">
              <div className="tw-m-2 tw-p-3">
                <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-3">
                  <div className="tw-col-span-12 md:tw-col-span-6">
                    <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-mb-3">
                      <div className="tw-col-span-12 sm:tw-col-span-4">Project name</div>
                      <div className="tw-col-span-12 sm:tw-col-span-8 tw-text-blue-600">{project?.name}</div>
                    </div>
                    <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-mb-3">
                      <div className="tw-col-span-12 sm:tw-col-span-4">Quotation / Job No</div>
                      <div className="tw-col-span-12 sm:tw-col-span-8 tw-text-blue-600">{project?.q_number}</div>
                    </div>
                  </div>
                  <div className="tw-col-span-12 md:tw-col-span-6">
                    <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-mb-3">
                      <div className="tw-col-span-12 sm:tw-col-span-4">Customer</div>
                      <div className="tw-col-span-12 sm:tw-col-span-8 tw-text-blue-600">{project?.customer.name}</div>
                    </div>
                  </div>
                </div>
                <div className="tw-grid tw-grid-cols-12 tw-gap-2">
                  <div className="tw-col-span-12 md:tw-col-span-4">
                    {contractField('Main contract value', 'main_contract_value', 'Main contract value', {
                      required: "Main contract value must be required.",
                      ...validateNumber()
                    })}
                    {contractField('Main contract weight', 'main_contract_weight', 'Main contract weight', {
                      required: "Main contract weight must be required.",
                      ...validateNumber()
                    })}
                  </div>
                  <div className="tw-col-span-12 md:tw-col-span-4">
                    {contractField('1st Addendum value', 'addendum_value_1st', '1st Addendum value', { ...validateNumber() })}
                    {contractField('1st Addendum weight', 'addendum_weight_1st', '1st Addendum weight', { ...validateNumber() })}
                  </div>
                  <div className="tw-col-span-12 md:tw-col-span-4">
                    {contractField('2st Addendum value', 'addendum_value_2st', '2st Addendum value', { ...validateNumber() })}
                    {contractField('2st Addendum weight', 'addendum_weight_2st', '2st Addendum weight', { ...validateNumber() })}
                  </div>
                </div>
              </div>
            </Accordion.Item>

            {/* 2. Payment history (for repeat customer) */}
            <Accordion.Item eventKey="1" header="2. Payment history (for repeat customer)">
              <div className="tw-m-2 tw-p-3">
                <textarea
                  rows={5}
                  placeholder="Payment history"
                  className={INPUT_CLASSES}
                  {...register("payment_history")}
                />
              </div>
            </Accordion.Item>

            {/* 3. Job Status */}
            <Accordion.Item eventKey="2" header="3. Job Status">
              <div className="tw-m-2 tw-p-3">
                <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-mb-2">
                  <div className="tw-col-span-12 md:tw-col-span-6">
                    {jobStatusCheck('Contract', 'job_status_contract')}
                    {jobStatusCheck('LOI', 'job_status_loi')}
                    {jobStatusCheck('JAF', 'job_status_jaf')}
                    {jobStatusCheck('BOM & JAF comparison ( For releasing shipment waiver)', 'job_status_bom_jaf_comparison')}
                  </div>
                  <div className="tw-col-span-12 md:tw-col-span-6">
                    <div className="tw-mb-2">Others</div>
                    <textarea
                      rows={5}
                      placeholder="Others"
                      className={INPUT_CLASSES}
                      {...register("job_status_other")}
                    />
                  </div>
                </div>
              </div>
            </Accordion.Item>

            {/* 4. Payment term */}
            <Accordion.Item eventKey="3" header="4. Payment term">
              <div className="tw-m-2 tw-p-3">
                {paymentTermField('1. Down payment', 'payment_term_dp', 'Down payment')}
                {paymentTermField('2. Second payment after delivery of M/F', 'payment_term_second', 'Second payment after delivery of M/F')}
                {paymentTermField('3. Third payment after deilivery of sheeting', 'payment_term_third', 'Third payment after deilivery of sheeting')}
                {paymentTermField('4. Four payment after completion of erection', 'payment_term_four', 'Four payment after completion of erection')}
                {paymentTermField('5. Final payment', 'payment_term_final', 'Final payment')}

                <div className="tw-mt-4">
                  <div className="tw-mb-2">Others</div>
                  <textarea
                    rows={5}
                    placeholder="Others"
                    className={INPUT_CLASSES}
                    {...register("payment_term_other")}
                  />
                </div>
              </div>
            </Accordion.Item>

            {/* 5. Release for */}
            <Accordion.Item eventKey="4" header="5. Release for">
              <div className="tw-m-2 tw-p-3">
                <div className="tw-grid tw-grid-cols-12 tw-gap-4">
                  <div className="tw-col-span-12 md:tw-col-span-6">
                    <div className="tw-mb-2">
                      {releaseForCheck('Anchor bolt delivery', 'released_for_anchor_bolt')}
                    </div>
                    <div className="tw-mb-2">
                      {releaseForCheck('Fabrication', 'released_for_fabrication')}
                    </div>
                    <div className="tw-mb-2">
                      {releaseForCheck('Loading', 'released_for_loading')}
                    </div>
                    <div className="tw-mb-2">
                      {releaseForCheck('Shipment', 'released_for_shipment')}
                    </div>
                    <div className="tw-mb-2">
                      <Controller
                        control={control}
                        name="released_for_order_material_check"
                        render={({ field }) => (
                          <FormCheckbox
                            checked={field.value || false}
                            label="Order material"
                            onChange={(e) => {
                              field.onChange(e)

                              const value = e.target.checked;
                              setValue("released_for_order_material", value ? getValues("released_for_order_material") : null);
                            }}
                          />
                        )}
                      />

                      <textarea
                        rows={4}
                        placeholder="None standard material"
                        className={`${INPUT_CLASSES} tw-mt-2`}
                        {...register("released_for_order_material")}
                        onChange={(e) => {
                          const value = e.target.value;
                          setValue("released_for_order_material_check", value !== "" && value !== null)
                          setValue("released_for_order_material", value);
                        }}
                      />
                      {fieldError(errors.released_for_order_material)}
                    </div>
                  </div>
                  <div className="tw-col-span-12 md:tw-col-span-6">
                    <div className="tw-mb-2">
                      {releaseForCheck('Approval dwgs & Estimate', 'released_for_apd_estimate')}
                    </div>
                    <div className="tw-mb-2">
                      {releaseForCheck('Shop dwgs', 'released_for_shop')}
                    </div>
                    <Controller
                      control={control}
                      name="released_for_other"
                      render={({ field }) => (
                        <FormCheckbox
                          checked={field.value || false}
                          label="Others (specify)"
                          onChange={(e) => {
                            field.onChange(e)

                            const value = e.target.checked;
                            setValue("released_for_other_specify", value ? getValues("released_for_other_specify") : null);
                          }}
                        />
                      )}
                    />

                    <textarea
                      rows={8}
                      placeholder="CAD file of APD/DSC/DSS..."
                      className={`${INPUT_CLASSES} tw-mt-2`}
                      {...register("released_for_other_specify")}
                      onChange={(e) => {
                        const value = e.target.value;
                        setValue("released_for_other", value !== "" && value !== null)
                      }}
                    />
                    {fieldError(errors.released_for_other_specify)}
                  </div>
                </div>
              </div>
            </Accordion.Item>

            {/* 6. Reason for waiver */}
            <Accordion.Item eventKey="5" header="6. Reason for waiver">
              <div className="tw-m-2 tw-p-3">
                <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-2 tw-items-center">
                  <div className="tw-col-span-12 sm:tw-col-span-5 lg:tw-text-left">
                    Waiver value (USD)
                  </div>
                  <div className="tw-col-span-12 sm:tw-col-span-7">
                    <input
                      placeholder="Waiver value"
                      className={INPUT_CLASSES}
                      {...register("waiver_value", { ...validateNumber() })}
                    />
                    {fieldError(errors.waiver_value)}
                  </div>
                </div>
                <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-2 tw-items-center">
                  <div className="tw-col-span-12 sm:tw-col-span-5 lg:tw-text-left">
                    Waiver weight (M.T)
                  </div>
                  <div className="tw-col-span-12 sm:tw-col-span-7">
                    <input
                      placeholder="Waiver weight"
                      className={INPUT_CLASSES}
                      {...register("waiver_weight", { ...validateNumber() })}
                    />
                    {fieldError(errors.waiver_weight)}
                  </div>
                </div>

                <div className="tw-mt-3 tw-mb-1">List out the reasons for the building</div>
                <div className="tw-overflow-x-auto">
                  <table className="tw-w-full tw-border-collapse tw-text-sm">
                    <thead>
                      <tr className="tw-bg-slate-50">
                        <th className={`${CELL_BORDER} tw-text-left`} width="5%"></th>
                        <th className={`${CELL_BORDER} tw-text-left`} width="5%">No.</th>
                        <th className={`${CELL_BORDER} tw-text-left`} width="10%">Building</th>
                        <th className={`${CELL_BORDER} tw-text-left`} width="80%">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {waiverBuildings && waiverBuildings.map((building, index) => (
                        <tr key={index} className="odd:tw-bg-white even:tw-bg-slate-50 hover:tw-bg-slate-100">
                          <td className={CELL_BORDER}>
                            <MdEdit className="tw-cursor-pointer tw-mr-2" onClick={() => handleEditBuilding(building)} />
                            <MdDelete className="tw-cursor-pointer" onClick={() => handleDeleteBuilding(index)} />
                          </td>
                          <td className={CELL_BORDER}>{index + 1}</td>
                          <td className={CELL_BORDER}>{building.name}</td>
                          <td className={CELL_BORDER}>
                            <span dangerouslySetInnerHTML={{ __html: building.reason }}></span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div
                  className="tw-flex tw-justify-end tw-items-center tw-text-blue-600 tw-cursor-pointer"
                  onClick={handleAddBuilding}
                >
                  <span className="circle-dashed">
                    <BsBuildingAdd />
                  </span>
                  <span className="tw-ml-3 tw-text-sm">Add Building</span>
                </div>

              </div>
            </Accordion.Item>

            {/* 7. Commitment date to clear waiver */}
            <Accordion.Item eventKey="6" header="7. Commitment date to clear waiver">
              <div className="tw-m-2 tw-p-3">
                <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-2 tw-items-center">
                  <div className="tw-col-span-12 sm:tw-col-span-5 lg:tw-text-left">
                    Commitment date
                  </div>
                  <div className="tw-col-span-12 sm:tw-col-span-7">
                    <Controller
                      control={control}
                      name="commitment_date"
                      rules={{ required: "Commitment date must be required" }}
                      render={({ field }) => (
                        <DatePicker
                          selected={field.value}
                          onChange={d => field.onChange(adjustToNoon(d))}
                          className={INPUT_CLASSES}
                          placeholderText="dd-MMM-YYYY"
                          dateFormat="dd-MMM-yyyy"
                          fixedHeight
                        />
                      )}
                    />
                    {fieldError(errors.commitment_date)}
                  </div>
                </div>
              </div>
            </Accordion.Item>

            {/* 8. Attachment */}
            {/* <Accordion.Item eventKey="7" header="8. Attachment">
              <div className="tw-m-2 tw-p-3">
                <UploadDocument files={files} setFiles={setFiles}/>
              </div>
            </Accordion.Item> */}

          </Accordion>

          <div className="tw-bg-slate-50 tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-border-0 tw-text-center tw-p-3 tw-z-10">
            <Button size="sm" type="submit" variant="primary" className="tw-mr-2">{waiver ? "Update" : "Create"} Waiver</Button>
            <Button size="sm" variant="danger" onClick={() => navigate(-1)}>Cancel</Button>
          </div>
        </form>
      </div>

      {showAddBuildingForm && (
        <AddBuildingForm
          showModal={showAddBuildingForm}
          setShowModal={setShowAddBuildingForm}
          project={project}
          setWaiverBuildings={setWaiverBuildings}
          building={building}
        />
      )}
    </>
  )
};

WaiverForm.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    q_number: PropTypes.string,
    customer: PropTypes.shape({
      name: PropTypes.string
    }),
    contract: PropTypes.shape({
      final_contract_value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      final_jaf_weight: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })
  }).isRequired,
  waiver: PropTypes.shape({
    id: PropTypes.number
  }).isRequired,
  title: PropTypes.string.isRequired
};

export default WaiverForm;
